import { useState, useCallback, useEffect } from "react";
import departmentService from "../services/departmentService";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import { FEATURES } from "../../config/features";

export const useDepartment = ({ showGeneralMessage, isMainModule = true }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        includeDivision: FEATURES.USE_DIM_DIVISION_DEPARTMENT,
        includeEmployeeType: FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT
      };

      const [deptRes, divRes, empTypeRes] = await Promise.all([
        departmentService.getAll(params), // REPLACED: Passing params
        FEATURES.USE_DIM_DIVISION_DEPARTMENT ? departmentService.getDivisions() : Promise.resolve({ data: [] }),
        FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT ? departmentService.getEmployeeTypes() : Promise.resolve({ data: [] }) // ADDED
      ]);

      setData(deptRes.data || []);
      setFilterData(deptRes.data || []);

      if (FEATURES.USE_DIM_DIVISION_DEPARTMENT) {
        const activeDivisions = divRes.data.filter(d => d.isActive !== false);
        setDivisionOptions(activeDivisions.map(d => ({ value: d.id, label: d.description })));
      }

      if (FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT) {
        const activeTypes = empTypeRes.data.filter(t => t.isActive !== false);
        setEmployeeTypeOptions(activeTypes.map(t => ({ value: t.id, label: t.description })));
      }
    } catch (err) {
      console.error("Failed to fetch department data", err);
      showGeneralMessage("Failed to load department data", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  const fetchActiveData = useCallback(async ({employeeTypeId = null, divisionId = null}) => {
    setLoading(true);
    try {
      const params = {
        includeDivision: FEATURES.USE_DIM_DIVISION_DEPARTMENT,
        includeEmployeeType: FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT,
        divisionId: divisionId || undefined,
        employeeTypeId: employeeTypeId || undefined,
      };

      const res = await departmentService.getAllActive(params);
      const responseData = res.data || [];
      setActiveData(responseData);
    } catch (err) {
      console.error("Failed to fetch departments", err);
      showGeneralMessage("Failed to load departments", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    // this will not auto load the module when it is not the main module
    if(!isMainModule) return;
    fetchData();
  }, [fetchData, isMainModule]);

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);
      if (Array.isArray(auditData) && auditData.length > 0) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData && !Array.isArray(auditData)) {
        await SetAuditTrail(auditData);
      }
      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchData();
      return { success: true };
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "An error occurred", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.description?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower) ||
      (FEATURES.DEPARTMENT_CODE && f.code?.toLowerCase().includes(lower)) ||
      (FEATURES.USE_DIM_DIVISION_DEPARTMENT && f.divisionDescription?.toLowerCase().includes(lower)) ||
      (FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT && f.employeeTypeDescription?.toLowerCase().includes(lower)) // ADDED
    ));
  };

  return {
    loading,
    data,
    activeData,
    divisionOptions,
    employeeTypeOptions,
    searchData,
    createDepartment: (p, audit) => handleAction(departmentService.create, audit, p),
    updateDepartment: (id, p, audit) => handleAction(departmentService.update, audit, id, p),
    refresh: fetchData,
    fetchActiveData
  };
};